import React, { useState, useEffect, useCallback } from 'react';
import './CaseDashboard.css';

// const DEFAULT_API = 'http://localhost/server/testServer';
const DEFAULT_API = 'https://www.albarede.com/server';

function CaseDashboard({ apiBase = DEFAULT_API }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // الـ State الخاص بالنموذج الإدخال والتعديل
  const [editingId, setEditingId] = useState(null); // null = وضع إضافة جديد
  const [formData, setFormData] = useState({
    case_number: '',
    client_name: '',
    status_title: '',
    status_slug: 'open',
    status_description: ''
  });

  // جلب كافة القضايا من قاعدة البيانات
  const fetchCases = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${apiBase}/manage_cases.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'fetch_all' })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) setCases(d.data);
        else setError(d.error || 'فشل في جلب بيانات القضايا.');
      })
      .catch(() => setError('خطأ في الاتصال بالسيرفر لتحديث القضايا.'))
      .finally(() => setLoading(false));
  }, [apiBase]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // معالجة تغيير المدخلات في الفورم
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // إرسال البيانات (إضافة أو تعديل) تِبعاً للوضع الحالي
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const actionType = editingId ? 'update' : 'create';
    const payload = {
      action: actionType,
      ...formData,
      ...(editingId && { id: editingId })
    };

    fetch(`${apiBase}/manage_cases.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setSuccessMsg(editingId ? 'تم تحديث بيانات القضية بنجاح!' : 'تم إضافة القضية الجديدة للنظام بنجاح!');
          handleResetForm();
          fetchCases();
        } else {
          setError(d.error || 'فشلت العملية القضائية الحالية.');
        }
      })
      .catch(() => setError('خطأ شبكة أثناء إرسال البيانات.'));
  };

  // حذف قضية من السيرفر
  const handleDelete = (id) => {
    if (!window.confirm('هل أنت متأكد تماماً من حذف هذه القضية؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    
    setError('');
    setSuccessMsg('');

    fetch(`${apiBase}/manage_cases.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id: id })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setSuccessMsg('تم حذف القضية من قاعدة البيانات بنجاح.');
          fetchCases();
        } else {
          setError(d.error || 'فشل الحذف.');
        }
      })
      .catch(() => setError('خطأ اتصال بالسيرفر أثناء الحذف.'));
  };

  // نقل البيانات للفورم وبدء وضع التعديل
  const handleEditClick = (c) => {
    setEditingId(c.id);
    setFormData({
      case_number: c.case_number,
      client_name: c.client_name,
      status_title: c.status_title,
      status_slug: c.status_slug,
      status_description: c.status_description || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // إلغاء التعديل وتصفير الفورم
  const handleResetForm = () => {
    setEditingId(null);
    setFormData({
      case_number: '',
      client_name: '',
      status_title: '',
      status_slug: 'open',
      status_description: ''
    });
  };

  return (
    <div className="cd-wrap" dir="rtl">
      <header className="cd-header">
        <h1>إدارة قضايا الموكلين</h1>
        <p>لوحة تحكم المشرف لإضافة القضايا وتعديل التقارير القانونية بشكل مباشر.</p>
      </header>

      {error && <div className="cd-alert error">{error}</div>}
      {successMsg && <div className="cd-alert success">{successMsg}</div>}

      {/* قسم الفورم: إضافة وتعديل القضايا */}
      <div className="cd-form-card">
        <h3>{editingId ? `تعديل القضية المرجعية: ${formData.case_number}` : 'إضافة قضية جديدة للنظام'}</h3>
        <form onSubmit={handleSubmit} className="cd-main-form">
          <div className="cd-form-grid">
            <div className="cd-field">
              <label>رقم القضية المرجعي (Unique Token) *</label>
              <input
                type="text"
                name="case_number"
                placeholder="مثال: CASE-2026-8941"
                value={formData.case_number}
                onChange={handleChange}
                required
              />
            </div>

            <div className="cd-field">
              <label>اسم الموكل كاملاً *</label>
              <input
                type="text"
                name="client_name"
                placeholder="مثال: أحمد بن ناصر"
                value={formData.client_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="cd-field">
              <label>عنوان حالة القضية الحالي (يظهر للموكل) *</label>
              <input
                type="text"
                name="status_title"
                placeholder="مثال: قيد الاستئناف / تم كسب الدعوى"
                value={formData.status_title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="cd-field">
              <label>نوع/رمز الحالة للتلوين الشارة</label>
              <select name="status_slug" value={formData.status_slug} onChange={handleChange}>
                <option value="open">مفتوحة حديثاً (Open)</option>
                <option value="in_court">قيد المرافعة بالمحكمة (In Court)</option>
                <option value="won">تم كسب القضية (Won)</option>
                <option value="closed">قضية مغلقة ومكتملة (Closed)</option>
                <option value="appealed">قيد الاستئناف (Appealed)</option>
              </select>
            </div>

            <div className="cd-field full-width">
              <label>التقرير القانوني والمستجدات التفصيلية (اختياري)</label>
              <textarea
                name="status_description"
                placeholder="اكتب هنا تفاصيل الجلسات أو الأوراق المطلوبة لتظهر للموكل عند الاستعلام..."
                rows={4}
                value={formData.status_description}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="cd-form-actions">
            <button type="submit" className="cd-btn primary-btn">
              {editingId ? 'حفظ التعديلات الحالية' : 'إضافة القضية للنظام'}
            </button>
            {editingId && (
              <button type="button" className="cd-btn ghost-btn" onClick={handleResetForm}>
                إلغاء وضع التعديل
              </button>
            )}
          </div>
        </form>
      </div>

      {/* قسم جدول استعراض وتعديل القضايا */}
      <div className="cd-list-card">
        <h3>قائمة القضايا المسجلة حالياً في النظام</h3>
        
        {loading ? (
          <div className="cd-loading">جاري جلب القضايا المحدثة من السيرفر...</div>
        ) : cases.length === 0 ? (
          <div className="cd-empty">لا يوجد أي قضايا مسجلة في النظام حالياً.</div>
        ) : (
          <div className="cd-table-responsive">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>الرقم المرجعي</th>
                  <th>اسم الموكل</th>
                  <th>الحالة القانونية</th>
                  <th>تاريخ الإضافة</th>
                  <th>إجراءات الإشراف</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id}>
                    <td><code>{c.case_number}</code></td>
                    <td className="font-weight-600">{c.client_name}</td>
                    <td>
                      <span className={`cd-badge slug-${c.status_slug}`}>
                        {c.status_title}
                      </span>
                    </td>
                    <td>{new Date(c.created_at).toLocaleDateString('ar-EG')}</td>
                    <td className="cd-actions-cell">
                      <button className="cd-action-link edit" onClick={() => handleEditClick(c)}>تعديل</button>
                      <button className="cd-action-link delete" onClick={() => handleDelete(c.id)}>حذف</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseDashboard;